import type { SimEvent } from "../sim/types";

/**
 * Audio 100% procedural: no hay archivos, no hay licencias, y el sonido puede
 * reaccionar al estado del juego de formas que una pista grabada no puede.
 *
 * Los digitos se mapean a una escala pentatonica, asi que cualquier respuesta
 * que teclees suena consonante por construccion.
 */

const PENTA = [0, 2, 4, 7, 9];

export class Audio {
  ctx: AudioContext | null = null;
  on = false;
  private master: GainNode | null = null;
  private delay: DelayNode | null = null;
  private drone: { o: OscillatorNode; g: GainNode } | null = null;
  private pulseAt = 0;

  init(): void {
    if (this.ctx) return;
    const AC = window.AudioContext ?? (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
    if (!AC) return;
    this.ctx = new AC();
    this.master = this.ctx.createGain();
    this.master.gain.value = 0.5;
    this.master.connect(this.ctx.destination);

    this.delay = this.ctx.createDelay();
    this.delay.delayTime.value = 0.14;
    const fb = this.ctx.createGain();
    fb.gain.value = 0.22;
    const wet = this.ctx.createGain();
    wet.gain.value = 0.3;
    this.delay.connect(fb);
    fb.connect(this.delay);
    this.delay.connect(wet);
    wet.connect(this.master);
  }

  async toggle(): Promise<boolean> {
    this.init();
    if (this.ctx && this.ctx.state === "suspended") await this.ctx.resume();
    this.on = !this.on;
    if (!this.on) this.freezeOff();
    return this.on;
  }

  private tone(
    f: number,
    dur: number,
    type: OscillatorType,
    g: number,
    when?: number,
    slideTo?: number,
  ): void {
    if (!this.on || !this.ctx || !this.master) return;
    const t0 = when ?? this.ctx.currentTime;
    const o = this.ctx.createOscillator();
    const gn = this.ctx.createGain();
    o.type = type;
    o.frequency.setValueAtTime(f, t0);
    if (slideTo) o.frequency.exponentialRampToValueAtTime(slideTo, t0 + dur * 0.85);
    gn.gain.setValueAtTime(0.0001, t0);
    gn.gain.exponentialRampToValueAtTime(g, t0 + 0.008);
    gn.gain.exponentialRampToValueAtTime(0.0001, t0 + dur);
    o.connect(gn);
    gn.connect(this.master);
    if (this.delay) gn.connect(this.delay);
    o.start(t0);
    o.stop(t0 + dur + 0.02);
  }

  private noise(dur: number, g: number, cut = 2400): void {
    if (!this.on || !this.ctx || !this.master) return;
    const n = Math.floor(this.ctx.sampleRate * dur);
    const buf = this.ctx.createBuffer(1, n, this.ctx.sampleRate);
    const d = buf.getChannelData(0);
    for (let i = 0; i < n; i++) d[i] = (Math.random() * 2 - 1) * (1 - i / n);
    const s = this.ctx.createBufferSource();
    s.buffer = buf;
    const f = this.ctx.createBiquadFilter();
    f.type = "lowpass";
    f.frequency.value = cut;
    const gn = this.ctx.createGain();
    gn.gain.value = g;
    s.connect(f);
    f.connect(gn);
    gn.connect(this.master);
    s.start();
  }

  private digitFreq(d: number): number {
    return 220 * Math.pow(2, (PENTA[d % 5]! + (d >= 5 ? 12 : 0)) / 12);
  }

  /** Consume los eventos que emitio la simulacion en este frame. */
  handle(events: SimEvent[]): void {
    for (const e of events) {
      switch (e.kind) {
        case "key":
          this.tone(this.digitFreq(e.digit), 0.34, "triangle", 0.16);
          break;
        case "fire":
          this.tone(1046 * Math.pow(1.12, e.idx), 0.08, "square", 0.06, undefined, 262);
          this.noise(0.04, 0.05, 3200);
          break;
        case "tick":
          this.noise(0.06, 0.09, 2200);
          break;
        case "boom":
          this.noise(e.big ? 0.42 : 0.2, e.big ? 0.24 : 0.13, e.big ? 820 : 1500);
          this.tone(e.big ? 68 : 110, e.big ? 0.44 : 0.22, "sine", 0.18, undefined, e.big ? 36 : 60);
          break;
        case "resolve": {
          if (!this.ctx) break;
          const t = this.ctx.currentTime;
          [1, 1.25, 1.5].forEach((m, i) =>
            this.tone(330 * m, 0.5, "triangle", 0.12, t + i * 0.045),
          );
          break;
        }
        case "chip": {
          // deslizamiento sin resolver: el oido sabe que la roca no murio
          const b = e.left > 1 ? 262 : 294;
          this.tone(b, 0.34, "sawtooth", 0.1, undefined, b * 1.9);
          break;
        }
        case "error": {
          if (!this.ctx) break;
          const t = this.ctx.currentTime;
          this.tone(233.1, 0.2, "sawtooth", 0.09, t);
          this.tone(329.6, 0.2, "sawtooth", 0.09, t);
          break;
        }
        case "breach":
          this.tone(62, 0.7, "sine", 0.34);
          this.noise(0.4, 0.2, 500);
          break;
        case "warp": {
          if (!this.ctx) break;
          const t = this.ctx.currentTime;
          this.tone(110, 0.9, "sine", 0.14, t, 660);
          this.noise(0.5, 0.1, 1200);
          [0, 0.12, 0.24].forEach((d, i) =>
            this.tone(440 * (1 + i * 0.25), 0.3, "triangle", 0.09, t + d),
          );
          break;
        }
        case "freezeOn":
          this.freezeOn();
          break;
        case "freezeOff":
          this.freezeOff();
          break;
      }
    }
  }

  private freezeOn(): void {
    if (!this.on || !this.ctx || !this.master) return;
    this.tone(1760, 0.6, "sine", 0.1, undefined, 110);
    const o = this.ctx.createOscillator();
    const g = this.ctx.createGain();
    o.type = "sine";
    o.frequency.value = 55;
    g.gain.value = 0.0001;
    g.gain.exponentialRampToValueAtTime(0.09, this.ctx.currentTime + 0.2);
    o.connect(g);
    g.connect(this.master);
    o.start();
    this.drone = { o, g };
  }

  private freezeOff(): void {
    if (!this.drone || !this.ctx) return;
    const { o, g } = this.drone;
    this.drone = null;
    g.gain.exponentialRampToValueAtTime(0.0001, this.ctx.currentTime + 0.3);
    o.stop(this.ctx.currentTime + 0.35);
    this.tone(110, 0.4, "sine", 0.1, undefined, 880);
  }

  /**
   * Cama ambiental. El ritmo del pulso sube con la ola y con la cercania de la
   * amenaza, asi que funciona como aviso de proximidad que se siente sin mirar.
   */
  bed(now: number, wave: number, threat: number): void {
    if (!this.on || !this.ctx) return;
    const iv = 560 - threat * 200 - Math.min(wave, 8) * 12;
    if (now < this.pulseAt) return;
    this.pulseAt = now + iv;
    this.tone(82.41, 0.34, "sine", 0.15);
    if (wave >= 2) this.tone(110, 0.26, "sine", 0.08, this.ctx.currentTime + iv / 2000);
    if (threat > 0.74) this.tone(880 + threat * 260, 0.06, "square", 0.06);
  }
}
